import md5 from 'md5';
import getCookie from './getCookie';
import { _ajax } from './__inner/image/_ajax';

interface UploadData {
  access_key_id: string;
  accessKey_secret: string;
  token: string;
  expire_at: number;
  bucket: string;
  oss_name: 'jd' | 'qiniu';
  endpoint: string;
  key: string;
  photo_id: number;
  upload_url: string;
  content_type: string;
}

export interface UploadImageOptions {
  ossName?: 'jd' | 'qiniu';
  type:
    | 'OPS'
    | 'BLOG'
    | 'ALBUM_COVER'
    | 'ARTICLE'
    | 'VIDEO'
    | 'COMMENT'
    | 'AVATAR'
    | 'PROFILE'
    | 'ACTIVITY'
    | 'SHORT_VIDEO'
    | 'SHORT_VIDEO_COVER'
    | 'LUT_FILE';
  host?: string;
  onprogress?: (event: ProgressEvent<EventTarget>) => void;
  onProgress?: (event: ProgressEvent<EventTarget>) => void;
  onUploadProgress?: (event: ProgressEvent<EventTarget>) => void;
  onApplyForToken?: (uploadData: UploadData) => void;
}

interface Response<T = any> {
  status: number;
  message: string;
  data: T;
}

export interface UploadResult {
  key: string;
  photo_width: number;
  photo_height: number;
  photo_id: number;
}

/**
 *
 * @param file 上传的文件
 * @param options { ossName: oss名称，默认'jd', type: 图片的用途，枚举, onProgress?: 下载进度回调, onprogress?: 下载进度回调, onUploadProgress?: 上传进度回调, onApplyForToken?: 拿到上传token等信息时的回调 }
 * @returns Promise<{ key: string; photo_width: number; photo_height: number; photo_id: number; }>
 */
const uploadImage = async (
  file: File,
  {
    ossName = 'jd',
    type,
    onprogress,
    onProgress,
    onUploadProgress,
    onApplyForToken,
    host = 'www.duitang.com',
  }: UploadImageOptions,
): Promise<UploadResult> => {
  try {
    const file_type = file.type.split('/')[1];
    const size = file.size;
    const mime_type = file.type;

    // 临时预览上传的图片宽高
    const img = new Image();
    img.src = URL.createObjectURL(file);
    // 这里不能用箭头函数 要让this指向函数调用者 即img
    return new Promise((resolve, reject) => {
      img.onload = async function () {
        const width = img.width;
        const height = img.height;
        let errorResponse;
        // 第一步 获取token 并拿到photo_id、key、oss_name
        // 根据ossName的值来判定是走七牛还是京东
        const uploadData = await _ajax({
          method: 'post',
          url: `https://${host}/napi/upload/token/photo/v3/`,
          data: JSON.stringify({
            type,
            file_type,
            size,
            mime_type,
            width,
            height,
            oss_name: ossName,
          }),
          headers: { 'content-type': 'application/json' },
          withCredentials: true,
        })
          .then((response: Response<UploadData>) => response.data)
          .catch(error => {
            errorResponse = error;
            return null;
          });

        if (uploadData) {
          const oss_name = uploadData.oss_name;
          const responseData = {
            key: uploadData.key,
            photo_width: width,
            photo_height: height,
            photo_id: uploadData.photo_id,
          };
          const reader = new FileReader();
          reader.readAsArrayBuffer(file);
          reader.onload = () => {
            // 如果是京东 走新接口
            if (oss_name === 'jd') {
              _ajax({
                url: uploadData.upload_url.replace('http://', 'https://'),
                method: 'PUT',
                headers: { 'content-type': mime_type },
                data: reader.result,
                onprogress,
                onProgress,
                onUploadProgress,
              })
                .then(() => {
                  _ajax({
                    method: 'POST',
                    data: JSON.stringify({
                      type,
                      file_type,
                      size,
                      mime_type,
                      width,
                      height,
                      photo_id: uploadData.photo_id,
                      key: uploadData.key,
                      hash: md5(reader.result as Uint8Array),
                    }),
                    headers: { 'content-type': 'application/json' },
                    url: `https://${host}/napi/upload/photo/confirm/`,
                    withCredentials: true,
                  }).then((response: Response<boolean>) => {
                    if (typeof onApplyForToken === 'function') {
                      onApplyForToken(uploadData);
                    }
                    if (response.data) {
                      resolve(responseData);
                    }
                  });
                })
                .catch(error => {
                  reject(error);
                });
            } else {
              // 是七牛 原有逻辑不变
              const uid = getCookie('_auth_user_id');
              const _fData = new FormData();
              _fData.append('file', file);
              _fData.append('token', uploadData.token);
              _fData.append('x:uid', uid);
              _fData.append('key', uploadData.key);
              _ajax({
                method: 'POST',
                data: _fData,
                url: 'https://up.qbox.me/',
                onprogress,
                onProgress,
                onUploadProgress,
              })
                .then(() => {
                  if (typeof onApplyForToken === 'function') {
                    onApplyForToken(uploadData);
                  }
                  resolve(responseData);
                })
                .catch(error => {
                  reject(error);
                });
            }
          };
        } else {
          reject(errorResponse);
        }
      };
    });
  } catch (error) {
    return Promise.reject(error);
  }
};

export default uploadImage;
